import { LitElement, PropertyValues, css, html, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import '@spectrum-web-components/bundle/elements.js'

import '../day-of-week/day-of-week'
import '../time-range/time-range'
import { OpeningHours } from '../../types/opening-hours'
import { translationKeys } from '../../translations'
import { currentLanguage as lang } from '../../common/i18n'
import { isOvernightHours, mergeOvernightPeriods } from '../../../../utils/time'
import { DAY_ORDER_FULL } from '../../../../utils/constants'

type TimeSlotDraft = {
  opens: string
  closes: string
}

type DayDraft = {
  dayOfWeek: string
  enabled: boolean
  slots: TimeSlotDraft[]
}

type DayView = DayDraft & {
  opens1: string
  closes1: string
  opens2?: string
  closes2?: string
  isOvernight1: boolean
  isOvernight2?: boolean
}

const CLOSED_SLOT: TimeSlotDraft = { opens: '00:00', closes: '00:00' }

export class RegularHours extends LitElement {

  @property({type: Array}) currentHours: OpeningHours[] = []
  @state() editHours: boolean = false
  @state() private draftDays: DayDraft[] = []

  save() {
    const source = this.draftDays.length > 0 ? this.draftDays : this.buildDayDrafts(this.currentHours)
    const newOpeningHours = this.draftToSchema(source)

    this.dispatchEvent(new CustomEvent('regularhourschange', { detail: newOpeningHours }))
    this.editHours = false
  }

  protected async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
    // Component initialization
  }

  private isEmptySlot(slot: TimeSlotDraft): boolean {
    return slot.opens === '00:00' && slot.closes === '00:00'
  }

  private buildDayDrafts(hours: OpeningHours[]): DayDraft[] {
    const dayMap = DAY_ORDER_FULL.reduce((acc: Record<string, { dayOfWeek: string, slots: TimeSlotDraft[] }>, day) => {
      acc[day] = { dayOfWeek: day, slots: [] }
      return acc
    }, {})

    hours.forEach(({ dayOfWeek, opens, closes }) => {
      if (!dayOfWeek || !dayMap[dayOfWeek]) return
      dayMap[dayOfWeek].slots.push({ opens, closes })
    })

    mergeOvernightPeriods(dayMap)

    return DAY_ORDER_FULL.map(day => {
      const slots = dayMap[day].slots
        .filter(slot => !this.isEmptySlot(slot))
        .sort((a, b) => a.opens.localeCompare(b.opens))

      return {
        dayOfWeek: day,
        enabled: slots.length > 0,
        slots,
      }
    })
  }

  private dayViewsFromDraft(days: DayDraft[]): DayView[] {
    return days.map(day => {
      const displaySlots = day.enabled && day.slots.length > 0 ? day.slots : [CLOSED_SLOT]
      const firstSlot = displaySlots[0] ?? CLOSED_SLOT
      const secondSlot = displaySlots[1]

      return {
        ...day,
        slots: displaySlots,
        opens1: firstSlot.opens,
        closes1: firstSlot.closes,
        opens2: secondSlot?.opens,
        closes2: secondSlot?.closes,
        isOvernight1: isOvernightHours(firstSlot.opens, firstSlot.closes),
        isOvernight2: secondSlot ? isOvernightHours(secondSlot.opens, secondSlot.closes) : false,
      }
    })
  }

  get mergedOpeningHours(): DayView[] {
    return this.dayViewsFromDraft(this.buildDayDrafts(this.currentHours))
  }

  private draftToSchema(days: DayDraft[]): OpeningHours[] {
    return days.flatMap(day => {
      const slots = day.enabled ? day.slots.filter(slot => !this.isEmptySlot(slot)) : []

      if (slots.length === 0) {
        return [{
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: day.dayOfWeek,
          opens: '00:00',
          closes: '00:00',
        } as OpeningHours]
      }

      return slots.map(slot => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: day.dayOfWeek,
        opens: slot.opens,
        closes: slot.closes,
      } as OpeningHours))
    })
  }

  private visibleEditSlots(day: DayDraft): TimeSlotDraft[] {
    if (!day.enabled || day.slots.length === 0) {
      return [CLOSED_SLOT, CLOSED_SLOT]
    }

    if (day.slots.length === 1) {
      return [day.slots[0], CLOSED_SLOT]
    }

    return day.slots
  }

  private startEdit() {
    this.draftDays = this.buildDayDrafts(this.currentHours)
    this.editHours = true
  }

  private cancelEdit() {
    this.draftDays = []
    this.editHours = false
  }

  private handleDraftRangeChange(evt: CustomEvent) {
    const target = evt.currentTarget as HTMLElement
    const slotIndex = Number(target.getAttribute('data-slot-index') ?? '0')
    const { name, from, to, off } = evt.detail as { name: string, from: string, to: string, off: boolean }
    const nextSlot = { opens: from, closes: to }

    this.draftDays = this.draftDays.map(day => {
      if (day.dayOfWeek !== name) return day

      if (slotIndex === 0 && off) {
        return { ...day, enabled: false, slots: [] }
      }

      const slots = [...day.slots]
      if (this.isEmptySlot(nextSlot)) {
        if (slotIndex > 0) {
          slots.splice(slotIndex, 1)
        } else if (slots.length > 0) {
          slots[0] = nextSlot
        }
      } else {
        slots[slotIndex] = nextSlot
      }

      return {
        ...day,
        enabled: true,
        slots: slots.filter(slot => !this.isEmptySlot(slot)),
      }
    })
  }

  render() {
    const tr = translationKeys[lang as keyof typeof translationKeys]
    const displayedHours = this.editHours
      ? this.dayViewsFromDraft(this.draftDays.length > 0 ? this.draftDays : this.buildDayDrafts(this.currentHours))
      : this.mergedOpeningHours
    
    return html`

      ${
        this.editHours 
        ? html`
       
        
          <form>
            ${
              displayedHours.map((o) => {
                const visibleSlots = this.visibleEditSlots(o)
                return html`
                <div class="day-row">
                  <time-range
                    .from=${ visibleSlots[0].opens }
                    .to=${ visibleSlots[0].closes }
                    .name=${ o.dayOfWeek }
                    .off=${ !o.enabled }
                    data-slot-index="0"
                    switch
                    hide-status
                    @timerangechange=${ this.handleDraftRangeChange }>
                    <day-of-week name=${ o.dayOfWeek } abbr></day-of-week>
                  </time-range>
                  ${visibleSlots.slice(1).map((slot, index) => html`
                    <div class="second-row">
                      ${index === 0 ? html`
                        <span class="status-badge ${o.enabled ? 'open' : 'closed'}">${o.enabled ? tr.isOpen : tr.isClosed}</span>
                      ` : html`<span></span>`}
                      <time-range
                        .from=${ slot.opens }
                        .to=${ slot.closes }
                        .name=${ o.dayOfWeek }
                        data-slot-index=${ String(index + 1) }
                        clear
                        compact
                        @timerangechange=${ this.handleDraftRangeChange }>
                      </time-range>
                    </div>
                  `)}
                </div>
                `
              })
            }
          </form>
           <sp-action-button quiet @click=${this.cancelEdit}>Abbrechen</sp-action-button>
           <sp-button @click=${this.save}>Speichern</sp-button>
      
        `
        : html`
          
            <ul>
              ${
              displayedHours.map((o) => {
                const openSlots = o.slots.filter(slot => !this.isEmptySlot(slot))
                return html`
                <li class="day-entry ${openSlots.some(slot => isOvernightHours(slot.opens, slot.closes)) ? 'overnight-hours' : ''}">
                  <div class="day-header">
                    <day-of-week name=${ o.dayOfWeek} abbr></day-of-week>
                  </div>
                  <div class="time-slots">
                    ${openSlots.length === 0 ? html`
                      <div class="closed-indicator">geschlossen</div>
                    ` : openSlots.map(slot => {
                      const overnight = isOvernightHours(slot.opens, slot.closes)
                      return html`
                        <div class="time-slot ${overnight ? 'overnight-slot' : ''}">
                          <span class="${overnight ? 'overnight-time' : ''}">${ slot.opens } - ${ slot.closes }</span>
                          ${overnight ? html`<span class="overnight-indicator">(${tr.overnightIndicator})</span>` : nothing}
                        </div>
                      `
                    })}
                  </div>
                </li>              
              `})
          }
          </ul>
<br />
          <sp-button @click=${this.startEdit}>Anpassen</sp-button>
        `
      }

    `
  }

  static styles = css`

    form {
      margin-bottom: 35px;
      padding: 14px 10px 14px 26px;
    }

    .day-row {
      display: grid;
      grid-template-columns: 90px 1fr;
      align-items: start;
      margin-bottom: 15px;
    }

    .day-row time-range {
      margin-bottom: 0;
      grid-column: 1 / -1;
    }

    /* Hide second row when day is closed */
    .day-row time-range[off][switch] + .second-row {
      display: none;
    }

    /* Second row with badge and second time interval */
    .second-row {
      display: contents;
    }

    .status-badge {
      font-size: 12px;
      font-weight: 600;
      padding: 3px 10px;
      border-radius: 10px;
      display: inline-block;
      white-space: nowrap;
      justify-self: start;
      align-self: center;
      margin-top: 4px;
    }

    .second-row time-range {
      grid-column: 2;
      margin-top: 4px;
    }

    .status-badge.open {
      background-color: #e8f5e9;
      color: #2e7d32;
    }

    .status-badge.closed {
      background-color: #ffebee;
      color: #c62828;
    }


    input {
      font-family: Roboto;
      color: #333;
    }

    li {
      list-style-type: none;
      margin-left: 0;
      margin-bottom: 15px;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 12px;
    }

    /* Day header */
    .day-header {
      min-width: 30px;
      flex-shrink: 0;
    }

    /* Time slots container */
    .time-slots {
      display: flex;
      flex-direction: column;
      gap: 6px;
      flex: 1;
    }

    /* Individual time slot */
    .time-slot {
      display: flex;
      align-items: center;
      gap: 8px;
      line-height: 1.4;
    }

    /* Overnight slot styling */
    .time-slot.overnight-slot {
      background-color: rgba(96, 155, 226, 0.08);
      border-radius: 4px;
      padding: 4px 8px;
    }

    /* Closed indicator */
    .closed-indicator {
      color: #666;
      font-style: italic;
    }

    /* Overnight time styling */
    .overnight-time {
      color: #1a5490;
      font-weight: 500;
    }

    /* Overnight indicator text */
    .overnight-indicator {
      font-size: 11px;
      color: #1a5490;
      font-style: italic;
    }

    /* Overnight hours styling for the entire day entry */
    li.overnight-hours {
      border-left: 3px solid rgba(96, 155, 226, 0.3);
      padding-left: 8px;
    }
    
    ul.regular li span:first-child {
      display: inline-block;
      width: 80px;
    }
    h1, h2, h3, h4, h5 {
      margin-top: 0;
      text-align: center;
      font-family: 'Asap Condensed';
    }
    ul {
      padding: 0;
      margin-bottom: 30px;
    }
/* --spectrum-switch-control-label-spacing: 5px; */

    section.view ul li span.bind {
      padding: 2px 14px;
    }

    sp-button {
      background-color: #609BE2;
      border-radius: 10px !important;
    }

/*
    input[type="time"]::-webkit-calendar-picker-indicator{
      filter: invert(0%) sepia(0%) saturate(3205%) hue-rotate(130deg) brightness(9.7%) contrast(90.96%);
    }
      */

    @media (max-width: 450px) {
      :host { padding: 0; background: none; }
    }

    @media (max-width: 385px) {
      :host { margin: 0; }
      section { width: 100%; }
    }
  `
}

if (!customElements.get('regular-hours')) {
  customElements.define('regular-hours', RegularHours)
}

declare global {
  interface HTMLElementTagNameMap {
    'regular-hours': RegularHours
  }
}
